"use client";

import { notifications } from "@mantine/notifications";
import { BannerActions } from "Components/Admin/AdminBanner/BannerActions";
import { BannerForm } from "Components/Admin/AdminBanner/BannerForm";
import { BannerTable } from "Components/Admin/AdminBanner/BannerTable";
import { Header } from "Components/Admin/StyleComponent/Header";
import { useMemo, useState } from "react";
import {
  useBulkDeleteBannersMutation,
  useCreateBannerMutation,
  useDeleteBannerMutation,
  useDeleteBannerImageMutation,
  useDeleteBanner2ImageMutation,
  useGetAllBannersWithoutBytesQuery,
  useUpdateBannerMutation,
  useUploadBanner2ImageMutation,
  useUploadBannerImageMutation,
  useGetMaxBannerLnNoQuery,
  useUpdateBannerOrderMutation,
} from "Services/Admin/BannerApiSlice";
import {
  BannerAdminRequestDTO,
  BannerWithoutBytesDTO,
} from "Types/Admin/AdminBannerType";

const PLANT = process.env.REACT_APP_PLANT;

const AdminBanner = () => {
  const {
    data: apiBanners,
    isLoading,
    refetch,
  } = useGetAllBannersWithoutBytesQuery();
  const banners: BannerWithoutBytesDTO[] = apiBanners?.results || [];

  const [createBanner] = useCreateBannerMutation();
  const [updateBanner] = useUpdateBannerMutation();
  const [deleteBanner] = useDeleteBannerMutation();
  const [deleteBannerImage] = useDeleteBannerImageMutation();
  const [deleteBanner2Image] = useDeleteBanner2ImageMutation();
  const [uploadBannerImage] = useUploadBannerImageMutation();
  const [uploadBanner2Image] = useUploadBanner2ImageMutation();
  const [bulkDeleteBanners] = useBulkDeleteBannersMutation();
  const [updateBannerOrder] = useUpdateBannerOrderMutation();
  const { data: maxLnNo } = useGetMaxBannerLnNoQuery();

  const handleDeleteDesktopImage = async () => {
    if (!editingBanner?.id) return;

    try {
      const res = await deleteBannerImage(editingBanner.id).unwrap();
      await refetch();
      notifications.show({
        message: res.message,
        color: "teal",
      });
    } catch (error: any) {
      console.error("Failed to delete desktop image:", error);
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const handleDeleteMobileImage = async () => {
    if (!editingBanner?.id) return;

    try {
      const res = await deleteBanner2Image(editingBanner.id).unwrap();
      await refetch();
      notifications.show({
        message: res.message,
        color: "teal",
      });
    } catch (error: any) {
      console.error("Failed to delete mobile image:", error);
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [editingBanner, setEditingBanner] =
    useState<BannerWithoutBytesDTO | null>(null);
  const [selectedBannerId, setSelectedBannerId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState("");
  const [selectedBannerIds, setSelectedBannerIds] = useState<number[]>([]);

  const filteredBanners = useMemo(() => {
    if (!banners) return [];
    const sortedBanners = [...banners].sort((a, b) => (a.bannerLnNo || 0) - (b.bannerLnNo || 0));
    return searchText
      ? sortedBanners.filter(
          (banner) =>
            (banner.title || "")
              .toLowerCase()
              .includes(searchText.toLowerCase()) ||
            (banner.bannerDescription || "")
              .toLowerCase()
              .includes(searchText.toLowerCase()),
        )
      : sortedBanners;
  }, [banners, searchText]);

  const handleReorderBanners = async (draggedId: number, targetId: number) => {
    const sorted = [...banners].sort((a, b) => (a.bannerLnNo || 0) - (b.bannerLnNo || 0));
    const draggedIndex = sorted.findIndex(b => b.id === draggedId);
    const targetIndex = sorted.findIndex(b => b.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const [draggedItem] = sorted.splice(draggedIndex, 1);
    sorted.splice(targetIndex, 0, draggedItem);

    const updatePayload = sorted.map((banner, index) => ({
      id: banner.id,
      lnNo: index + 1
    }));

    try {
      const result = await updateBannerOrder(updatePayload).unwrap();
      await refetch();
      notifications.show({ message: result.message, color: "teal" });
    } catch (error: any) {
      console.error("Failed to reorder banners:", error);
      notifications.show({ message: error?.data?.message || "An error occurred", color: "red" });
    }
  };

  const bannerMappingFromWithoutBytesToRequest = (
    banner: BannerWithoutBytesDTO | null,
  ) => {
    if (banner != null) {
      return {
        buttonText: banner.buttonText || "",
        content: banner.content || "",
        description: banner.bannerDescription || "",
        link: banner.link || "",
        lnNo: banner.bannerLnNo,
        title: banner.title || "",
      } as BannerAdminRequestDTO;
    } else {
      return null;
    }
  };

  const handleViewBanner = async (id: number) => {
    try {
      const bannerToView = banners.find((banner) => banner.id === id);
      if (bannerToView) {
        setEditingBanner(bannerToView);
        setViewMode(true);
        setShowForm(true);
      }
    } catch (error) {
      console.error("Failed to get banner details:", error);
    }
  };

  const handleEditBanner = async (id: number) => {
    try {
      const bannerToEdit = banners.find((banner) => banner.id === id);
      if (bannerToEdit) {
        setEditingBanner(bannerToEdit);
        setViewMode(false);
        setShowForm(true);
      }
    } catch (error) {
      console.error("Failed to get banner details:", error);
    }
  };

  const handleSubmit = async (
  data: BannerAdminRequestDTO,
  imageFile?: File,
  mobileImageFile?: File,
) => {
  try {
    if (!editingBanner && banners.length >= 20) {
      return;
    }

    let bannerId = editingBanner?.id;
    let successMessage: string;

    if (editingBanner) {
      const updatedData = {
        bannerDescription: data.description,
        bannerLnNo: data.lnNo,
        title: data.title,
        content: data.content,
        buttonText: data.buttonText,
        link: data.link,
        plant: PLANT!,
      };

      const updateRes = await updateBanner({
        id: editingBanner.id,
        data: updatedData,
      }).unwrap();
      successMessage = updateRes.message;
    } else {
      const apiMax = maxLnNo?.results ? Number(maxLnNo.results) : 0;
      const localMax = banners.reduce((m, b) => Math.max(m, b.bannerLnNo || 0), 0);
      const nextLnNo = Math.max(apiMax, localMax) + 1;
      const createData = {
        ...data,
        lnNo: nextLnNo,
        plant: PLANT!,
      };

      const resultDTO = await createBanner(createData).unwrap();
      bannerId = resultDTO.results as number;
      successMessage = resultDTO.message;
    }

    if (bannerId) {
      if (imageFile) {
        try {
          await uploadBannerImage({
            id: bannerId,
            file: imageFile,
          }).unwrap();
        } catch (error) {
          console.error("Desktop image upload failed:", error);
        }
      }

      if (mobileImageFile) {
        try {
          await uploadBanner2Image({
            id: bannerId,
            file: mobileImageFile,
          }).unwrap();
        } catch (error) {
          console.error("Mobile image upload failed:", error);
        }
      }
    }

    setShowForm(false);
    setEditingBanner(null);

    notifications.show({
      message: successMessage,
      color: "teal",
    });

    await refetch();
  } catch (error: any) {
    console.error("Failed to save banner:", error);

    notifications.show({
      message: error?.data?.message || "An error occurred",
      color: "red",
    });
  }
};

  const handleDeleteBanner = async (id: number) => {
    try {
      const res = await deleteBanner(id).unwrap();

      const remainingBanners = [...banners]
        .filter(b => b.id !== id)
        .sort((a, b) => (a.bannerLnNo || 0) - (b.bannerLnNo || 0));
      const updatePayload = remainingBanners.map((banner, index) => ({
        id: banner.id,
        lnNo: index + 1
      }));
      if (updatePayload.length > 0) {
        await updateBannerOrder(updatePayload).unwrap();
      }

      await refetch();
      notifications.show({
        message: res.message,
        color: "teal",
      });
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
      console.error("Failed to delete banner:", error);
    }
  };

  const handleBulkDeleteBanners = async (ids: number[]) => {
    try {
      const res = await bulkDeleteBanners({ ids }).unwrap();

      const remainingBanners = [...banners]
        .filter(b => !ids.includes(b.id))
        .sort((a, b) => (a.bannerLnNo || 0) - (b.bannerLnNo || 0));
      const updatePayload = remainingBanners.map((banner, index) => ({
        id: banner.id,
        lnNo: index + 1
      }));
      if (updatePayload.length > 0) {
        await updateBannerOrder(updatePayload).unwrap();
      }

      await refetch();
      setSelectedBannerIds([]);
      notifications.show({
        message: res.message,
        color: "teal",
      });
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
      console.error("Failed to delete banners:", error);
    }
  };
if (isLoading) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
}
  return (
    <div className="font-body p-6 mx-auto">
      {!showForm ? (
        <>
          <Header
            title="Banners"
            onAdd={() => banners.length < 20 && setShowForm(true)}
            addLabel="Add Banner"
            addButtonDisabled={banners.length >= 20}
            addButtonTooltip={
              banners.length >= 20 ? "Maximum banners limit reached" : undefined
            }
          />
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden p-4 pt-8">
            <BannerActions
              onSearchChange={setSearchText}
              isDeleteDisabled={selectedBannerIds.length === 0}
              deleteCount={selectedBannerIds.length}
              onDelete={() => handleBulkDeleteBanners(selectedBannerIds)}
            />
            <BannerTable
              banners={filteredBanners}
              onDeleteBanner={handleDeleteBanner}
              onViewBanner={handleViewBanner}
              onEditBanner={handleEditBanner}
              onSelectBanner={(ids) => setSelectedBannerIds(ids || [])}
              onReorderBanners={handleReorderBanners}
              selectedBanner={selectedBannerIds}
            />
          </div>
        </>
      ) : (
        <>
          <button
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4"
            onClick={() => {
              setShowForm(false);
              setEditingBanner(null);
              setViewMode(false);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-arrow-left-icon lucide-arrow-left h-4 w-4"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
            <span className="text-sm">Back</span>
          </button>
          <Header
            title={
              viewMode
                ? "View Banner"
                : editingBanner
                  ? "Edit Banner"
                  : "Add Banner"
            }
          />
          <BannerForm
            initialValues={
              bannerMappingFromWithoutBytesToRequest(editingBanner) || {
                title: "",
                description: "",
                content: "",
                buttonText: "",
                link: "",
                lnNo: 0,
              }
            }
            existingImage={editingBanner?.bannerPath || undefined}
            existingMobileImage={editingBanner?.bannerPathTwo || undefined}
            onDeleteDesktopImage={handleDeleteDesktopImage}
            onDeleteMobileImage={handleDeleteMobileImage}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setViewMode(false);
              setEditingBanner(null);
            }}
            mode={viewMode ? "view" : editingBanner ? "edit" : "add"}
          />
        </>
      )}
    </div>
  );
};

export default AdminBanner;
